import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Price } from '@hero/web-components';
import { ChevronDownIcon, FileTextIcon, InvoiceIcon, ArrowUpRightIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import type { ApplicationMock } from '../mocks';
import { ApplicationRowDetails } from './ApplicationRowDetails';
import { useOpenHeroInvoiceDrawer } from './useOpenHeroInvoiceDrawer';
import { StatusBadge } from './StatusBadge';

/** Day in milliseconds. */
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Derived "where is this application in its repayment timeline?" used
 * by the Due column. Mirrors the invoice-table pattern from
 * `useInvoiceInfo` (`isOverdue`, `isDueToday`, `isPaidOnTime`,
 * `isPaidEarly`, `daysLeft`) but is scoped to the lifecycle states
 * the financing pilot actually exposes here:
 *
 *  - `paid_on_time`   — `repaid` AND `repaidAt` is on/before `dueDate`.
 *  - `paid_late`      — `repaid` AND `repaidAt` is after `dueDate`.
 *  - `overdue`        — not yet `repaid`, `dueDate` is past
 *                       (carrier-visible signal even before the
 *                       lender flips the application to defaulted).
 *  - `due_today`      — not yet `repaid`, `dueDate` matches today's
 *                       calendar day.
 *  - `upcoming`       — `dueDate` is in the future.
 *  - `unknown`        — no `dueDate` resolved yet (e.g. pre-funded
 *                       row); the column shows an em-dash so the
 *                       layout doesn't shift between rows.
 *
 * Returns the absolute day delta so the badge can render
 * `"3 days left"` / `"5 days late"` etc. without re-deriving the
 * date math in the JSX.
 */
type DueState =
  | { kind: 'paid_on_time'; days: number }
  | { kind: 'paid_late'; days: number }
  | { kind: 'overdue'; days: number }
  | { kind: 'due_today' }
  | { kind: 'upcoming'; days: number }
  | { kind: 'unknown' };

function getDueState(application: ApplicationMock, now = new Date()): DueState {
  if (!application.dueDate) return { kind: 'unknown' };
  const due = new Date(application.dueDate);
  if (Number.isNaN(due.getTime())) return { kind: 'unknown' };

  // Compare at calendar-day granularity so a wire that lands at the
  // same date but a few hours apart doesn't read as "1 day late".
  const dueDay = startOfDay(due).getTime();
  const todayDay = startOfDay(now).getTime();

  if (application.status === 'repaid' && application.repaidAt) {
    const repaidDay = startOfDay(new Date(application.repaidAt)).getTime();
    const delta = Math.round((repaidDay - dueDay) / DAY_MS);
    return delta <= 0
      ? { kind: 'paid_on_time', days: Math.abs(delta) }
      : { kind: 'paid_late', days: delta };
  }

  const delta = Math.round((dueDay - todayDay) / DAY_MS);
  if (delta < 0) return { kind: 'overdue', days: Math.abs(delta) };
  if (delta === 0) return { kind: 'due_today' };
  return { kind: 'upcoming', days: delta };
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export type ApplicationsTableProps = {
  applications: ApplicationMock[];
  /**
   * Hide the title/description header. Use when the host card already
   * supplies its own header (e.g. the dashboard "Recent" card) so the
   * heading doesn't render twice.
   */
  hideHeader?: boolean;
  /**
   * When set, auto-expands the matching application row — used by
   * the invoice drawer's "View application" deep link
   * (`?applicationId=` on the financing history tab).
   */
  expandApplicationId?: string | null;
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const formatTermShort = (
  t: ReturnType<typeof useTranslation>['t'],
  a: ApplicationMock,
) => {
  const { value, unit } = a.requestedTerm;
  const key = unit === 'days' ? `${value}_days` : unit === 'months' ? '6_months' : '1_year';
  return t(`term.${key}`);
};

export function ApplicationsTable({
  applications,
  hideHeader,
  expandApplicationId,
}: ApplicationsTableProps) {
  const { t } = useTranslation('m-financing');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const openHeroInvoice = useOpenHeroInvoiceDrawer();

  useEffect(() => {
    if (!expandApplicationId) return;
    setExpanded(prev => {
      if (prev.has(expandApplicationId)) return prev;
      const next = new Set(prev);
      next.add(expandApplicationId);
      return next;
    });
  }, [expandApplicationId]);

  const toggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (applications.length === 0) {
    return (
      <section className="rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-10 text-center">
        {!hideHeader && (
          <div className="mx-auto flex size-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <FileTextIcon className="size-6" />
          </div>
        )}
        <h3 className="mt-3 text-base font-semibold text-gray-900">
          {t('applications_table.empty.title')}
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          {t('applications_table.empty.description')}
        </p>
      </section>
    );
  }

  return (
    <section>
      {!hideHeader && (
        <div className="mb-4 flex items-baseline justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">
              {t('applications_table.title')}
            </h2>
            <p className="mt-0.5 text-sm text-gray-600">
              {t('applications_table.description')}
            </p>
          </div>
          <Badge variant="secondary" size="small">
            {applications.length}
          </Badge>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/80 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th scope="col" className="w-10" />
                <th scope="col" className="px-4 py-3 text-start font-medium">
                  {t('applications_table.columns.ref')}
                </th>
                <th scope="col" className="px-4 py-3 text-start font-medium">
                  {t('applications_table.columns.invoice')}
                </th>
                <th scope="col" className="px-4 py-3 text-start font-medium">
                  {t('applications_table.columns.requested')}
                </th>
                <th scope="col" className="px-4 py-3 text-start font-medium">
                  {t('applications_table.columns.status')}
                </th>
                <th scope="col" className="px-4 py-3 text-start font-medium">
                  {t('applications_table.columns.due')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {applications.map(app => {
                const isExpanded = expanded.has(app.id);
                const InvoiceSourceIcon =
                  app.invoice.source === 'hero' ? InvoiceIcon : FileTextIcon;

                return (
                  <Fragment key={app.id}>
                    <tr
                      className={cn(
                        'cursor-pointer transition-colors hover:bg-emerald-50/40',
                        isExpanded && 'bg-emerald-50/30',
                      )}
                      onClick={() => toggle(app.id)}>
                      <td className="px-3 py-3">
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            toggle(app.id);
                          }}
                          aria-label={
                            isExpanded
                              ? t('applications_table.collapse_label')
                              : t('applications_table.expand_label')
                          }
                          className={cn(
                            'flex size-7 items-center justify-center rounded-md text-gray-500 transition-transform hover:bg-gray-100',
                            isExpanded && 'rotate-180 bg-gray-100',
                          )}>
                          <ChevronDownIcon className="size-4" />
                        </button>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-gray-800">
                        {app.ref}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2">
                          <span
                            className={cn(
                              'flex size-7 shrink-0 items-center justify-center rounded-lg',
                              app.invoice.source === 'hero'
                                ? 'bg-primary-50 text-primary-700'
                                : 'bg-gray-100 text-gray-700',
                            )}
                            title={t(`invoice_source.${app.invoice.source}`)}>
                            <InvoiceSourceIcon className="size-3.5" />
                          </span>
                          {app.invoice.source === 'hero' && app.invoice.id ? (
                            <button
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                openHeroInvoice(app.invoice.id!);
                              }}
                              className="group min-w-0 flex-1 rounded-md p-0.5 text-start transition-colors hover:bg-gray-100"
                              title={t('applications_table.invoice.open_cta', {
                                defaultValue: 'Open invoice',
                              })}>
                              <div className="flex items-start justify-between gap-1">
                                <div className="min-w-0 flex-1">
                                  <div className="truncate text-sm font-medium text-gray-900">
                                    {app.invoice.clientName}
                                  </div>
                                  <div className="truncate text-xs text-gray-500">
                                    <Price
                                      amount={app.invoice.amount}
                                      currency={app.invoice.currency}
                                    />
                                  </div>
                                </div>
                                <ArrowUpRightIcon className="mt-0.5 size-3.5 shrink-0 text-gray-400 transition-colors group-hover:text-gray-600" />
                              </div>
                            </button>
                          ) : (
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-medium text-gray-900">
                                {app.invoice.clientName}
                              </div>
                              <div className="truncate text-xs text-gray-500">
                                <Price
                                  amount={app.invoice.amount}
                                  currency={app.invoice.currency}
                                />
                              </div>
                              {app.invoice.invoiceNumber ? (
                                <div className="mt-0.5 truncate text-xs text-gray-400">
                                  #{app.invoice.invoiceNumber}
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <Price
                          amount={app.requestedAmount}
                          currency={app.invoice.currency}
                          className="font-semibold text-gray-900"
                        />
                        <div className="text-xs text-gray-500">{formatTermShort(t, app)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-start gap-1">
                          <StatusBadge kind="application" value={app.status} />
                          {app.isLate && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 ring-1 ring-amber-200">
                              {t('tags.late', { defaultValue: 'Late' })}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <DueCell application={app} />
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr>
                        <td colSpan={6} className="p-0">
                          <ApplicationRowDetails application={app} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/**
 * Due-date cell — renders `MMM d, yyyy` on top with a color-coded
 * "days left / late / paid early" pill underneath. Visual taxonomy
 * matches the invoice table (`InvoiceTableCell.getDaysLeftBadge`) so
 * the carrier reads both surfaces the same way:
 *
 *  - emerald — repaid on/before due, or repaid early
 *  - red     — repaid late, or unpaid and past due
 *  - amber   — unpaid, due today
 *  - muted   — unpaid, future
 *
 * Rows without a resolved `dueDate` (pre-funded) render the date as
 * an em-dash and skip the pill so the column doesn't expand the row
 * height with an empty placeholder.
 */
function DueCell({ application }: { application: ApplicationMock }) {
  const { t } = useTranslation('m-financing');
  const state = getDueState(application);

  if (state.kind === 'unknown') {
    return <span className="text-xs text-gray-400">{'—'}</span>;
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <span className="text-sm font-medium text-gray-900">
        {application.dueDate ? formatDate(application.dueDate) : '—'}
      </span>
      <DueBadge state={state} t={t} />
    </div>
  );
}

function DueBadge({
  state,
  t,
}: {
  state: Exclude<DueState, { kind: 'unknown' }>;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const base =
    'inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium';
  switch (state.kind) {
    case 'paid_on_time':
      return (
        <span className={cn(base, 'bg-emerald-50 text-emerald-700')}>
          {state.days > 0
            ? t('applications_table.due.paid_early', { count: state.days })
            : t('applications_table.due.paid_on_time')}
        </span>
      );
    case 'paid_late':
      return (
        <span className={cn(base, 'bg-red-50 text-red-700')}>
          {t('applications_table.due.paid_late', { count: state.days })}
        </span>
      );
    case 'overdue':
      return (
        <span className={cn(base, 'bg-red-50 text-red-700')}>
          {t('applications_table.due.days_late', { count: state.days })}
        </span>
      );
    case 'due_today':
      return (
        <span className={cn(base, 'bg-amber-50 text-amber-700')}>
          {t('applications_table.due.due_today')}
        </span>
      );
    case 'upcoming':
      return (
        <span className={cn(base, 'bg-gray-100 text-gray-600')}>
          {t('applications_table.due.days_left', { count: state.days })}
        </span>
      );
  }
}
